import * as React from "react";
import { useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { CssBaseline } from "@mui/material";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Home from "./components/Home";
import About from "./components/About";
import DatasetCreator from "./components/dataset/DatasetCreator";
import Sidebar from "./components/navigation/Sidebar";
import Tour from "./components/Tour";
import ExperimentTypeSelector from "./components/experiment/ExperimentTypeSelector";
import Examples from "./components/Examples";
import ExperimentConfigurator from "./components/experiment/ExperimentConfigurator";
import ExperimentConfiguratorErrorModel from "./components/experiment/ExperimentConfiguratorErrorModel";
import Overview from "./components/Overview";
import NotFound from "./components/NotFound";
import DemoDatasets from "./components/dataset/DemoDatasets";
import DatasetExplorer from "./components/dataset/DatasetExplorer";
import ExperimentHeader from "./components/experiment/ExperimentHeader";
import { fetchData } from "./utils/api";
import { initKeycloak } from "./utils/auth";
import {
  getSelectedDatasetByRoute,
  getSelectedExperimentByRoute,
  updateURL,
} from "./utils/routing_utils";
import packageConfig from "../package.json";
import theme from "./theming";
import ExperimentRoutes from "./components/experiment/ExperimentRoutes";
import SplitDataPage from "./components/dataset/SplitDataPage";

// context for the current user settings
export const UserContext = React.createContext();

function App() {
  // variables to track the currently selected dataset, experiment and it's progress
  const [datasets, setDatasets] = React.useState([]);
  const [experiments, setExperiments] = React.useState([]);
  const [currentDatasetIdx, setCurrentDatasetIdx] = React.useState(0);
  const [currentExperimentIdx, setCurrentExperimentIdx] = React.useState(0);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const [experimentsLoaded, setExperimentsLoaded] = React.useState(false);
  // experiment progress describes, which steps the user has already conducted
  const [currentExperimentProgress, setCurrentExperimentProgress] =
    React.useState();
  const [datasetsLoaded, setDatasetsLoaded] = React.useState(false);
  // DemoOnly set userid by local storage
  const [storedUid, setStoredUid] = React.useState("unset");

  const fetchDatasets = ({ latest = false }) => {
    // get all Datasets for the current user
    fetchData(`${process.env.REACT_APP_BACKEND_URL}/api/v0/datasets`).then(
      (data) => {
        if (data.datasets.length > 0) {
          const names = data.datasets.map((dict) => dict.datasetName);
          // set datasets and use first dataset as default (if no latest is given)
          let dsIdx = 0;
          // set current selection to given latest (if the name still exists in list)
          if (latest && names.indexOf(latest) !== -1) {
            dsIdx = names.indexOf(latest);
          }
          setCurrentDatasetIdx(dsIdx);
          setDatasets(data.datasets);
        } else {
          // if no datasets exist, set to defaults and declare data loading as done
          setDatasets([]);
          setExperiments([]);
        }
        setDatasetsLoaded(true);
      }
    );
  };
  const fetchExperiments = ({ latest = false }) => {
    // get all Experiments for the currently selected dataset
    const currentDataset = datasets[currentDatasetIdx];
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${currentDataset.datasetName}/experiments`
    ).then((data) => {
      // set experiments and use first entry as default
      setExperiments(data.experiments);
      if (data.experiments.length > 0) {
        let newSelectedExperimentIdx = 0;
        const names = data.experiments.map((dict) => dict.experimentName);
        // set current selection to given latest (if the name exists in list)
        if (latest && names.indexOf(latest) !== -1) {
          newSelectedExperimentIdx = names.indexOf(latest);
        }
        setCurrentExperimentIdx(newSelectedExperimentIdx);
        // set progress of current Experiment
        setCurrentExperimentProgress(
          data.experiments[newSelectedExperimentIdx].experimentProgress
        );
        setDataLoaded(true);
      } else {
        // if no experiments exist, declare data loading as done
        setDataLoaded(true);
      }
      setExperimentsLoaded(true)
    });
  };

  // set user and datasets on the first render
  useEffect(() => {
    // init keycloak then fetch datasets
    initKeycloak().then(() => {
      setStoredUid(window.localStorage.getItem("automl-uid"));
      if (dataLoaded) {
        fetchDatasets({});
      } else {
        fetchDatasets({ latest: getSelectedDatasetByRoute() });
      }
    });
  }, []);

  // Callback Method, everytime the selected dataset changes
  useEffect(() => {
    if (datasets.length > [0]) {
      fetchExperiments({ latest: getSelectedExperimentByRoute() });
    } else {
      setExperiments([]);
    }
  }, [datasets, currentDatasetIdx]);

  // Callback Method, everytime the selected experiment changes
  useEffect(() => {
    // only update, if datasets and experiments exist
    if (datasets.length > 0 && experiments.length > 0) {
      // set progress of current Experiment
      setCurrentExperimentProgress(
        experiments[currentExperimentIdx].experimentProgress
      );
      // update URL according to new selection
      updateURL(datasets, currentDatasetIdx, experiments, currentExperimentIdx);
    }
  }, [...[experiments], currentExperimentIdx]);

  // dynamically switch favicon
  useEffect(() => {
    const favIcon = document.getElementById("favIcon");
    switch (process.env.REACT_APP_THEME) {
      case "KILAB":
        favIcon.href = `${process.env.PUBLIC_URL}/kilab_favIcon.svg`;
        break;
      default:
        favIcon.href = `${process.env.PUBLIC_URL}/mdz_favIcon.ico`;
    }
  }, []);

  const { i18n } = useTranslation();

  // set language on page load
  useEffect(() => {
    const languageSet = window.localStorage.getItem("languageSet");
    if (languageSet === null) {
      window.localStorage.setItem("languageSet", i18n.language);
    } else if (languageSet !== i18n.language) {
      // setLanguage(languageSet);
      i18n.changeLanguage(languageSet);
    }
  }, []);

  return (
    <ThemeProvider theme={theme(process.env.REACT_APP_THEME)}>
      <UserContext.Provider
        value={{
          storedUid,
          datasets,
          setDatasets,
          fetchDatasets,
          experiments,
          setExperiments,
          fetchExperiments,
          currentDatasetIdx,
          setCurrentDatasetIdx,
          currentExperimentIdx,
          setCurrentExperimentIdx,
          currentExperimentProgress,
          setCurrentExperimentProgress,
          experimentsLoaded,
        }}
      >
        {
          // wait for the data to be loaded
          datasetsLoaded ? (
            <Router
              basename={new URL(packageConfig.homepage).pathname.replace(
                /\/$/,
                ""
              )}
            >
              <Box sx={{ display: "flex" }} className="automl-app">
                <CssBaseline />
                <Sidebar />
                <Tour userIsLoggedIn />
                <Box
                  component="main"
                  className="content-box"
                  sx={{
                    ...theme().typography.main,
                    // ml: { xs: 0, md: "100px" }, // margin-left matches Sidebar width
                    width: { xs: "100%", md: "calc(100% - 100px)" },
                    minHeight: "100vh",
                    bgcolor: "background.default",
                    p: { xs: 1.5, sm: 3 },
                    overflow: "auto",
                  }}
                >
                  <Routes>
                    <Route path="/" exact element={<Home />} />
                    {/* @To-Do:Remove this /kilab route asap the prod domain has moved */}
                    <Route path="/kilab" exact element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route
                      path="/new-experiment"
                      element={<ExperimentTypeSelector />}
                    />
                    <Route
                      path="/experiment-configuration"
                      element={<ExperimentConfigurator />}
                    /><Route
                        path="/experiment-configuration-error-model"
                        element={<ExperimentConfiguratorErrorModel/>}
                    />
                    {process.env.REACT_APP_IS_DEMO === "true" ? ( // overwrite routes for demo
                      <>
                        <Route path="/examples" element={<Examples />} />
                        <Route path="/new-dataset" element={<DemoDatasets />} />
                      </>
                    ) : (
                      <>
                        <Route path="/examples" element={<Examples />} />
                        <Route
                          path="/demo-datasets"
                          element={<DemoDatasets />}
                        />
                        <Route
                          path="/new-dataset"
                          element={<DatasetCreator />}
                        />
                        <Route
                          path="/split-data/:datasetName"
                          element={<SplitDataPage />}
                        />
                      </>
                    )}

                    <Route path="/overview" element={<Overview />} />
                    <Route
                      path="/data-explorer"
                      element={<DatasetExplorer />}
                    />
                    <Route
                      path="/experimentAnalyzeHeader"
                      element={<ExperimentHeader />}
                    />

                    {
                      // Experiment Routes: Content is dependent on current dataset and current experiment
                      // eslint-disable-next-line no-nested-ternary
                      datasets.length > 0 &&
                      experiments.length > 0 &&
                      currentExperimentProgress ? (
                        <>
                          <Route
                            path="/quality-check/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="qualityCheck" />}
                          />
                          <Route
                            path="/inspect/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="inspect" />}
                          />
                          <Route
                            path="/train/:dataset?/:experiment?/pipeline/:run?"
                            element={<ExperimentRoutes route="pipeline" />}
                          />
                          <Route
                            path="/train/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="train" />}
                          />
                          <Route
                            path="/analyze/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="analyze" />}
                          />
                          <Route
                            path="/runs/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="runs" />}
                          />
                        </>
                      ) : datasets.length > 0 ? (
                        <>
                          <Route
                            path="/quality-check/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="NoExperiment" />}
                          />
                          <Route
                            path="/inspect/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="NoExperiment" />}
                          />
                          <Route
                            path="/train/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="NoExperiment" />}
                          />
                          <Route
                            path="/analyze/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="NoExperiment" />}
                          />
                          <Route
                            path="/runs/:dataset?/:experiment?"
                            element={<ExperimentRoutes route="NoExperiment" />}
                          />
                        </>
                      ) : (
                        ""
                      )
                    }

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Box>
              </Box>

              <div className="Footer" />
              {/* <Routes> */}
              {/*    <Route path="/demonstrator" element={ */}
              {/*        <Box sx={{padding: theme.spacing(4)}}><Demonstrator></Demonstrator></Box> */}
              {/*    }></Route> */}
              {/* </Routes> */}
            </Router>
          ) : (
            ""
          )
        }
      </UserContext.Provider>
    </ThemeProvider>
  );
}

export default App;
